/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import PropTypes from "prop-types";
import React from "react";
import SigPad from "signature_pad";
import autoBind from "react-autobind";
import _debounce from "lodash/debounce";

class SignaturePad extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      currentCanvasWidth: 0,
      currentCanvasHeight: 0,
    };
    this.canvasRef = React.createRef();
  }

  componentDidMount() {
    if (this.canvasRef.current) {
      if (!this.props.width || !this.props.height) {
        this.canvasRef.current.style.width = "100%";
      }
      this.scaleCanvas();
      this.signaturePadInstance = new SigPad(this.canvasRef.current, this.props.options);
      if (!this.props.width || !this.props.height) {
        window.addEventListener("resize", _debounce(this.scaleCanvas, this.props.debounceInterval));
      }
      this.signaturePadInstance.onEnd = () => {
        this.props.setMinimumSignatureSizeReached(this.signaturePadInstance.toDataURL("image/svg+xml").length > 2000);
      };
    }
  }

  componentWillUnmount() {
    if (!this.props.width || !this.props.height) {
      window.removeEventListener("resize", _debounce(this.scaleCanvas, this.props.debounceInterval));
    }
    this.signaturePadInstance.off();
  }

  scaleCanvas = () => {
    if (!this.canvasRef.current) {
      return;
    }

    const context = this.canvasRef.current.getContext("2d");
    const imageData = context.getImageData(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvasWidth = (this.props.width || this.canvasRef.current.offsetWidth) * ratio;
    const canvasHeight = (this.props.height || this.canvasRef.current.offsetHeight) * ratio;

    if (canvasWidth === this.state.currentCanvasWidth && canvasHeight === this.state.currentCanvasHeight) {
      return;
    }

    this.canvasRef.current.width = canvasWidth;
    this.canvasRef.current.height = canvasHeight;
    this.setState({
      currentCanvasWidth: this.canvasRef.current.width,
      currentCanvasHeight: this.canvasRef.current.height,
    });

    this.canvasRef.current.getContext("2d").scale(ratio, ratio);
    this.signaturePadInstance && this.signaturePadInstance.clear();
    context.putImageData(imageData, 0, 0);
  };

  clear() {
    this.signaturePadInstance.clear();
    this.props.setMinimumSignatureSizeReached(false);
  }

  render() {
    const { options, debounceInterval, redrawOnResize, setMinimumSignatureSizeReached, disabled, ...canvasProps } = this.props;

    const canvasStyle = {
      pointerEvents: disabled ? 'none' : 'auto',
      opacity: disabled ? 0.5 : 1
    };

    return <canvas ref={this.canvasRef} style={canvasStyle} {...canvasProps} />;
  }
}

export default SignaturePad;

SignaturePad.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  setMinimumSignatureSizeReached: PropTypes.func.isRequired,
  debounceInterval: PropTypes.number,
  redrawOnResize: PropTypes.bool,
  options: PropTypes.object,
  disabled: PropTypes.bool,
};

SignaturePad.defaultProps = {
  debounceInterval: 150,
  redrawOnResize: false,
  disabled: false
};
